import { useCallback, useContext, useEffect, useState } from 'react';
import UsersContext from '../../../contexts/UsersContext';
import STORE from '../../../store';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import isDefined from '../../../isDefined';
import useLockedDebouncedEffect from '../../../hooks/useLockedDebouncedEffect';
import getUserAccountAccess from '../../../api/getUserAccountAccess';
import CloudFunctionClientContext from '../../../contexts/CloudFunctionClientContext';

interface GetUnMappedUsersArgs {
  signedUpAndPendingUsers: (
    | UserManagement.SignedUpUser
    | UserManagement.PendingUser
  )[];
}

const useUnMappedUsers = (entityId: string, entityAppId: string) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { signedUpAndPendingUsers, isLoading: isLoadingUsers } =
    useContext(UsersContext);
  const [unMappedUsers, setUnMappedUsers] = useState<
    (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getBasicUsers = useCallback(
    async (args: GetUnMappedUsersArgs) => {
      const { signedUpAndPendingUsers } = args;
      const promises = signedUpAndPendingUsers
        .filter((u) => !u.isDeleted)
        .map(async (user) => {
          const r = await getUserAccountAccess(user.id, api);
          if (!r.ok || !r.data) {
            return {
              user,
            };
          }
          const roles = r.data.userProfile?.roles;
          if (!roles) {
            return {
              user,
            };
          }

          return {
            user,
            roles,
          };
        });

      const usersWithRoles = await Promise.all(promises);

      const basicUsers = usersWithRoles
        .filter((user) => {
          if (!user.roles) {
            return false;
          }
          return user.roles.some((r) => r === 'fleetops.roles.viewer');
        })
        .map((u) => u.user);

      return basicUsers;
    },
    [api],
  );

  const getUnMappedUsers = useCallback(
    async (
      args: GetUnMappedUsersArgs,
    ): Promise<
      (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
    > => {
      setIsLoading(true);
      const basisUsers = await getBasicUsers(args);

      const promises = basisUsers.map(
        async (
          user,
        ): Promise<
          undefined | UserManagement.SignedUpUser | UserManagement.PendingUser
        > => {
          const userContentSettings = await STORE.userManagement
            .getUserContentSettingsRef({
              accountId: selectedAccountId,
              userId: user.id,
            })
            .get();

          if (!userContentSettings.exists) {
            return user;
          }
          const settings = userContentSettings.data();
          if (!settings) {
            return user;
          }
          if (settings.mode === 'normal') {
            return user;
          }

          const isThisUsedMappedToThisEntity = settings.mappings.some(
            (mapping) =>
              mapping.entityId === entityId &&
              mapping.entityAppId === entityAppId,
          );

          if (!isThisUsedMappedToThisEntity) {
            return user;
          }

          return undefined;
        },
      );

      const results = await Promise.all(promises);
      return results.filter(isDefined);
    },
    [entityAppId, entityId, getBasicUsers, selectedAccountId],
  );

  const buildArgs = useCallback((): GetUnMappedUsersArgs | undefined => {
    if (isLoadingUsers) {
      return undefined;
    }

    return {
      signedUpAndPendingUsers,
    };
  }, [signedUpAndPendingUsers, isLoadingUsers]);
  const [args, setArgs] = useState<GetUnMappedUsersArgs | undefined>(() =>
    buildArgs(),
  );
  useEffect(() => {
    setArgs(buildArgs());
  }, [buildArgs]);

  const handleResponse = useCallback(
    (
      response: (UserManagement.PendingUser | UserManagement.SignedUpUser)[],
    ) => {
      setUnMappedUsers(response);
      setIsLoading(false);
    },
    [],
  );

  useLockedDebouncedEffect({
    args,
    responseHandler: handleResponse,
    callback: getUnMappedUsers,
  });

  return { unMappedUsers, isLoading };
};

export default useUnMappedUsers;
